import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Clock, MessageCircle, Heart, Instagram, Facebook, Linkedin, ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';
import { useLang } from '../useLang';
import { images, stepMeta, socialMeta } from '../siteConfig';

const iconMap = { Mail, Clock, MessageCircle, Heart, Instagram, Facebook, Linkedin };

const ThankYouPage = () => {
  const { lang, t } = useLang();
  const ty = t.thankyou;

  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="bg-cream min-h-screen">
      <section className="max-w-3xl mx-auto px-5 md:px-8 pt-16 pb-12 text-center">
        <div className="w-24 h-28 mx-auto rounded-full flex items-center justify-center fade-up" style={{ background: 'var(--gold)' }}>
          <Heart className="w-12 h-12 text-white" fill="white" />
        </div>
        <h1 className="font-head font-extrabold text-5xl md:text-6xl mt-8" style={{ color: 'var(--navy)' }}>{ty.heading}</h1>
        <p className="font-script text-2xl md:text-3xl mt-3 leading-snug" style={{ color: 'var(--coral)' }}>
          {ty.script}
        </p>
        <p className="font-body text-lg mt-6 max-w-xl mx-auto leading-relaxed" style={{ color: 'var(--ink)' }}>
          {ty.intro}
        </p>
      </section>

      {/* WHAT HAPPENS NEXT */}
      <section className="max-w-3xl mx-auto px-5 md:px-8 pb-12">
        <div className="bg-cream-deep rounded-3xl p-8 md:p-12">
          <h2 className="font-head font-extrabold tracking-wide text-center text-xl md:text-2xl mb-9" style={{ color: 'var(--navy)' }}>
            {ty.whatNext}
          </h2>
          <div className="space-y-7">
            {ty.steps.map((s, i) => {
              const meta = stepMeta[i];
              const Icon = iconMap[meta.icon];
              return (
                <div key={i} className="flex items-start gap-5">
                  <div className="w-14 h-14 shrink-0 rounded-full flex items-center justify-center" style={{ background: meta.color }}>
                    <Icon className="w-7 h-7 text-white" strokeWidth={1.8} />
                  </div>
                  <div className="pt-1">
                    <h3 className="font-head font-bold text-lg" style={{ color: 'var(--navy)' }}>{s.title}</h3>
                    <p className="font-body text-base leading-relaxed" style={{ color: 'var(--ink)' }}>{s.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REMEMBER */}
      <section className="max-w-5xl mx-auto px-5 md:px-8 py-10 grid md:grid-cols-2 gap-10 items-center">
        <div>
          <p className="font-script text-4xl mb-5" style={{ color: 'var(--navy)' }}>{ty.rememberHeading}</p>
          <div className="font-body text-lg leading-relaxed space-y-3" style={{ color: 'var(--ink)' }}>
            {ty.remember.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </div>
        <div className="overflow-hidden rounded-3xl shadow-lg">
          <img src={images.cozy} alt="Rachelle Koulibaly" className="w-full h-72 object-cover object-[center_25%]" />
        </div>
      </section>

      {/* STAY CONNECTED */}
      <section className="bg-cream-deep">
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-14 text-center">
          <h2 className="font-head font-extrabold tracking-wide text-xl md:text-2xl" style={{ color: 'var(--navy)' }}>{ty.stayConnected}</h2>
          <p className="font-body text-lg mt-2 mb-9" style={{ color: 'var(--ink)' }}>{ty.follow}</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {ty.socials.map((s, i) => {
              const meta = socialMeta[i];
              const Icon = iconMap[meta.icon];
              return (
                <a key={i} href="#" className="flex items-center gap-3 justify-center bg-white rounded-xl py-4 px-5 hover-lift border border-black/[0.04]">
                  <Icon className="w-7 h-7 shrink-0" style={{ color: meta.color }} />
                  <span className="text-left">
                    <span className="font-head font-bold text-sm block" style={{ color: 'var(--navy)' }}>{s.label}</span>
                    <span className="font-body text-xs" style={{ color: 'var(--ink)' }}>{s.handle}</span>
                  </span>
                </a>
              );
            })}
          </div>
          <Link to={`/${lang}`} className="inline-flex items-center gap-2 mt-10 font-head font-semibold hover:gap-3 transition-all" style={{ color: 'var(--coral)' }}>
            <ArrowLeft className="w-4 h-4" /> {ty.backHome}
          </Link>
        </div>
      </section>

      <Footer tagline={t.footer.thankYouTagline} subline={t.footer.thankYouSubline} />
    </div>
  );
};

export default ThankYouPage;
